import type { ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { operationTypeNames, operationTypeTone, useAuditLogDetail } from './useAuditLogs';

/**
 * One audit entry in full — the port of `_AuditLogDetailsModal.cshtml`.
 *
 * Same layout: paired fields two to a row, then the full-width user agent,
 * detail and metadata blocks, with the error only shown when there is one.
 */
export interface AuditLogDetailsModalProps {
  logId: number;
  onClose: () => void;
}

/** Label above value, as the Razor `<strong>` + `<p>` pairs rendered. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <strong>{label}:</strong>
      <p className="mb-0">{children}</p>
    </div>
  );
}

/**
 * Pretty-prints a JSON payload, falling back to the raw text.
 *
 * The Detail and Metadata columns hold serialised objects, but nothing
 * guarantees it — an older entry may hold a plain string, and that should still
 * be readable rather than blank.
 */
function JsonBlock({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted">—</span>;

  let text = value;
  try {
    text = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    /* not JSON; show it as it is */
  }

  return (
    <pre className="json-viewer bg-body-secondary rounded p-2 mb-0 small text-break" style={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </pre>
  );
}

export function AuditLogDetailsModal({ logId, onClose }: AuditLogDetailsModalProps) {
  const { t } = useTranslation();
  const { data: log, isLoading, isError } = useAuditLogDetail(logId);

  return (
    <Modal show onHide={onClose} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{t('AuditLogs.Details', 'Audit log details')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading && (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            {t('Loading', 'Loading...')}
          </div>
        )}

        {isError && (
          <div className="alert alert-danger mb-0" role="alert">
            {t('AuditLogs.DetailsError', 'Could not load this log entry.')}
          </div>
        )}

        {log && (
          <>
            <div className="row">
              <div className="col-md-6">
                <Field label={t('AuditLogs.LogId', 'Log ID')}>{log.id}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.OperationDate', 'Operation date')}>
                  {new Date(log.operationDate).toLocaleString()}
                </Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.Owner', 'Owner')}>{log.owner || '—'}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.UserId', 'User ID')}>{log.userId || '—'}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.ObjectType', 'Object type')}>
                  {log.objectType || '—'}
                </Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.ObjectId', 'Object ID')}>{log.objectId || '—'}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.Operation', 'Operation')}>
                  <span className={`badge ${operationTypeTone[log.operationType] ?? 'bg-secondary'}`}>
                    {operationTypeNames[log.operationType] ?? log.operationType}
                  </span>
                </Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.Action', 'Action')}>{log.action || '—'}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.IpAddress', 'IP address')}>{log.ipAddress || '—'}</Field>
              </div>
              <div className="col-md-6">
                <Field label={t('AuditLogs.Status', 'Status')}>
                  {log.isSuccess ? (
                    <span className="badge bg-success">{t('AuditLogs.Success', 'Success')}</span>
                  ) : (
                    <span className="badge bg-danger">{t('AuditLogs.Failed', 'Failed')}</span>
                  )}
                </Field>
              </div>
            </div>

            <div className="mb-3">
              <strong>{t('AuditLogs.UserAgent', 'User agent')}:</strong>
              <p className="mb-0 text-break">{log.userAgent || '—'}</p>
            </div>

            {/* Only when the operation actually failed. */}
            {log.errorMessage && (
              <div className="mb-3">
                <strong>{t('AuditLogs.ErrorMessage', 'Error message')}:</strong>
                <div className="alert alert-danger mb-0 mt-1">{log.errorMessage}</div>
              </div>
            )}

            <div className="mb-3">
              <strong>{t('AuditLogs.Detail', 'Detail')}:</strong>
              <div className="mt-1">
                <JsonBlock value={log.detail} />
              </div>
            </div>

            {log.metadata && (
              <div className="mb-0">
                <strong>{t('AuditLogs.Metadata', 'Metadata')}:</strong>
                <div className="mt-1">
                  <JsonBlock value={log.metadata} />
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t('Close', 'Close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
